import type { DateTimeFormats } from 'vue-i18n';

import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { LANG_COUNTRY_MAPPING, WEB_LOCALES } from '@/constants/web-locales.constant';

const APP_TRANSLATION_URL = `${JW_CONFIG.ASSETS_URL}/translations/webapp/`;
const SPORT_TRANSLATION_URL = `${JW_CONFIG.ASSETS_URL}/translations/sports_webapp/`;

const existingLocales = new Set(Object.values(WEB_LOCALES));

/** Lokalise uses double mustache ({{}}) for interpolation, vue i18n expects single mustache ({}). */
function transformTranslations(translations: Record<string, string>) {
	for (const key in translations) {
		translations[key] = translations[key].replace(/{{([a-zA-Z-_]+)}}/g, '{$1}');
	}

	return translations;
}

/** Builds the correct translation_LOCALE with fallbacks and edge cases. */
function constructTranslationLocale(language: string, locale: string) {
	// Some languages have special locale behavior like es-419 for Latin American Spanish.
	// They need custom handling to request the appropriate translation file.
	const [lang, localeCompound] = language.split('-');
	if (localeCompound != null) {
		return `${lang}_${LANG_COUNTRY_MAPPING[language].toUpperCase()}`;
	}

	const constructedLocale = `${language}_${locale.toUpperCase()}`;
	if (existingLocales.has(constructedLocale)) {
		return constructedLocale;
	}

	// the en.json file has CORS errors for some reason, so for now,
	// we fetch the "default" british english. #temporaryIsForever
	if (language === 'en') {
		return 'en_GB';
	}

	return language;
}

/** Fetches the translation file at the given url and transforms the result for the app. */
async function fetchTranslations(url: string, language: string, locale: string) {
	const languageFile = `${constructTranslationLocale(language, locale)}.json`;

	try {
		const response = await fetch(new URL(languageFile, url).href);
		if (!response.ok) {
			throw new Error(`[Fetch Translation] Received non 2xx response. Response: ${response.status}`);
		}

		const data: Record<string, string> = await response.json();

		return transformTranslations(data ?? {});
	} catch (error) {
		// Do not crash SSR on translation fetch failure — fall back to bundled English.
		console.error('[Fetch Translation] Failed, falling back to bundled en.json:', error);
		const { default: fallback } = await import('./en.json');
		captureMessageForSentry(
			'[Fetch Translation]',
			{ error: error as any, where: '[language-helpers.ts]: fetchTranslations' },
			'error'
		);

		return transformTranslations({ ...fallback });
	}
}

/** Fetches the WEBAPP_ translation for the language_LOCALE pair. */
export async function fetchAppTranslations(language: string, locale: string) {
	return fetchTranslations(APP_TRANSLATION_URL, language, locale);
}

/** Fetches the SPORTS_WEBAPP_ translation for the language_LOCALE pair. */
export async function fetchSportsTranslations(language: string, locale: string) {
	return fetchTranslations(SPORT_TRANSLATION_URL, language, locale);
}

export const dateTimeFormats: DateTimeFormats = {
	'en-US': {
		short: {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		},
		long: {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			weekday: 'long',
			hour: 'numeric',
			minute: 'numeric',
		},
	},
	'de-DE': {
		short: {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		},
		long: {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			weekday: 'long',
			hour: 'numeric',
			minute: 'numeric',
		},
	},
} as DateTimeFormats;
